<script setup>
import { ref } from 'vue'
import { useDisplay } from 'vuetify'

const props = defineProps({
  persistent: { type: Boolean, default: false },
  hideCloseButton: { type: Boolean, default: false },
  hideCancelButton: { type: Boolean, default: false },
  maxWidth: { type: String, default: '800px' },
  cardClass: { type: String, default: '' },
  cardColor: { type: String, default: '' },
  dialogProps: { type: Object, default: () => ({}) },
  cardProps: { type: Object, default: () => ({}) },
  bodyClass: { type: String, default: '' },
  actionClass: { type: String, default: '' },
  activator: { type: null, default: undefined },
  closeButtonProps: { type: Object, default: () => ({}) },
  headerWrapperClass: { type: String, default: '' },
  topBarClass: { type: String, default: '' },
})
const showDialog = defineModel({ type: Boolean, default: false })

const { mobile } = useDisplay()

const bodyRef = ref(null)
defineExpose({ bodyRef })
</script>

<template>
  <v-dialog
    v-model="showDialog"
    scrollable
    style="backdrop-filter: blur(3px); --webkit-backdrop-filter: blur(3px)"
    v-bind="props.dialogProps"
    :max-width="props.maxWidth"
    :persistent="props.persistent"
    :activator="props.activator"
  >
    <v-dialog-card
      v-bind="props.cardProps"
      width="100%"
      :class="[props.cardClass, 'd-flex flex-column']"
    >
      <div
        v-if="$slots.topBar"
        :class="props.topBarClass"
        class="flex-shrink-0"
      >
        <slot name="topBar" />
      </div>

      <div
        v-if="$slots.title || $slots.headerIcon || !props.hideCloseButton"
        class="d-flex flex-row justify-space-between align-center flex-shrink-0"
        :class="props.headerWrapperClass"
        :style="{ padding: mobile ? '0 8px' : '0 12px' }"
      >
        <div class="d-flex align-center ga-2 text-truncate">
          <slot name="headerIcon" />
          <v-card-title class="px-0 flex-shrink-1 text-truncate">
            <slot name="title" />
          </v-card-title>
        </div>
        <div
          v-if="!props.hideCloseButton || $slots.headerActions"
          class="d-flex align-center ga-2"
        >
          <slot name="headerActions" />
          <v-btn
            v-if="!props.hideCloseButton"
            v-bind="props.closeButtonProps"
            class="rounded-circle"
            icon="mdi-close"
            :size="mobile ? 'small' : 'x-small'"
            variant="plain"
            @click="showDialog = false"
          >
            <v-icon
              icon="mdi-close"
              size="large"
            />
          </v-btn>
        </div>
      </div>

      <div
        v-if="$slots.body"
        ref="bodyRef"
        :class="props.bodyClass"
        class="flex-grow-1 overflow-y-auto"
        style="width: 100%; min-height: 0"
        :style="{ padding: mobile ? '8px' : '12px' }"
      >
        <slot name="body" />
      </div>

      <v-card-actions
        v-if="$slots.actions"
        class="justify-end flex-wrap flex-shrink-0"
        :class="props.actionClass"
        :style="{ padding: mobile ? '4px 8px' : '8px 12px' }"
      >
        <v-btn
          v-if="!props.hideCancelButton"
          class="px-2"
          variant="outlined"
          @click="showDialog = false"
        >
          <v-icon icon="mdi-close" />
          {{ $t('general_interface.buttons.cancel') }}
        </v-btn>
        <slot name="actions" />
      </v-card-actions>
    </v-dialog-card>
  </v-dialog>
</template>
